/**
 * `conversation_quality_digest` — the weekly report on the messaging agents: how they did, what waits for a person,
 * what self-learning changed and the questions left unresolved. Names agents and questions, never a customer.
 */

import { z } from 'zod'

import { CHANGE_STATES } from '@/lib/conversation-quality/change-display'
import { TOPIC_CATEGORIES } from '@/lib/conversation-quality/outcomes'

import type { TenantEventDefinition } from './definition'

export const CONVERSATION_QUALITY_DIGEST_EVENT_TYPE = 'conversation_quality_digest' as const

export const DIGEST_TOP_QUESTIONS = 5
export const DIGEST_TOP_AGENTS = 3
export const DIGEST_LEARNING_LINES = 3

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** `of` out of `over`, as the page's shares count them. */
const CountsSchema = z.object({ of: z.number().int().min(0), over: z.number().int().min(0) })

/** The week's share and the week before's, which the change is read from. */
const WeekShareSchema = z.object({ current: CountsSchema, previous: CountsSchema })

export const ConversationQualityDigestPayloadSchema = z
  .object({
    /** The report's Monday; its links open the page on this exact UTC week. */
    periodStart: z.string().regex(DATE_ONLY),
    /** The week's last day, inclusive. */
    periodEnd: z.string().regex(DATE_ONLY),
    figures: z.object({
      requests: z.number().int().min(0),
      resolved: WeekShareSchema,
      handedToTeam: WeekShareSchema,
      /** Customers nobody has answered yet, as the Overview's tile counts them. */
      customersWaiting: z.number().int().min(0),
    }),
    /** The busiest agents of the week. */
    agents: z
      .array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
          requests: z.number().int().positive(),
          resolved: CountsSchema,
        }),
      )
      .max(DIGEST_TOP_AGENTS),
    /** Every change waiting for a person now, and the newest of them. */
    waiting: z.object({
      count: z.number().int().min(0),
      first: z
        .object({
          suggestionId: z.string().uuid(),
          agentId: z.string().uuid(),
          agentName: z.string(),
          /** «wants to rewrite a line you wrote». */
          ask: z.string(),
          question: z.string(),
        })
        .nullable(),
    }),
    /** What self-learning did in the run the report follows, worded as the Changes list words it. */
    learning: z.object({
      /** Changes the platform made by itself, and changes it took back. */
      learned: z.number().int().min(0),
      takenBack: z.number().int().min(0),
      /** The one agent behind all of it; `null` for several. */
      learner: z.string().nullable(),
      lines: z
        .array(z.object({ question: z.string(), state: z.enum(CHANGE_STATES), detail: z.string() }))
        .max(DIGEST_LEARNING_LINES),
    }),
    topQuestions: z
      .array(z.object({ label: z.string(), category: z.enum(TOPIC_CATEGORIES), sessions: z.number().int().positive() }))
      .max(DIGEST_TOP_QUESTIONS),
    /** Sent on demand from Notification Channels: a real week's content, outside the weekly schedule. */
    test: z.literal(true).optional(),
  })
  // A week without requests has nothing to report on the agents.
  .refine((payload) => payload.figures.requests > 0)

export type ConversationQualityDigestPayload = z.infer<typeof ConversationQualityDigestPayloadSchema>

export const conversationQualityDigestEventDefinition: TenantEventDefinition<ConversationQualityDigestPayload> =
  {
    eventType: CONVERSATION_QUALITY_DIGEST_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: (payload): payload is ConversationQualityDigestPayload =>
      ConversationQualityDigestPayloadSchema.safeParse(payload).success,
    tenant: {
      label: 'Conversation Quality digest',
      description:
        'Weekly, for every week the agents had conversations: how they did, what waits for a person, what self-learning changed and the questions they didn\'t resolve.',
      recipients: 'own_card',
      mandatory: false,
    },
  }
